import { useCallback } from 'react';
import { logEvent, setUserProperties, trackPageView } from '../services/analytics';

/**
 * Custom hook for analytics functionality
 * @returns {object} Analytics functions
 */
export const useAnalytics = () => {
  // Track user actions
  const trackEvent = useCallback(async (eventName, eventParams = {}) => {
    await logEvent(eventName, eventParams);
  }, []);

  // Track page views
  const trackPage = useCallback(async (pageName, pageTitle = '') => {
    await trackPageView(pageName, pageTitle || pageName);
  }, []);

  // Set user properties when user logs in or profile changes
  const setUser = useCallback(async (userProperties) => {
    await setUserProperties(userProperties);
  }, []);

  // Track quiz completion
  const trackQuizCompletion = useCallback(async (quizData) => {
    await logEvent('quiz_completed', {
      quiz_type: quizData.quiz_type || 'general',
      questions_completed: quizData.questions_completed || 0,
      completion_source: quizData.source || 'unknown',
      ...quizData
    });
  }, []);

  // Track account creation
  const trackAccountCreation = useCallback(async (accountData) => {
    await logEvent('sign_up', {
      method: accountData.method || 'email',
      source: accountData.source || 'direct',
      user_id: accountData.user_id,
      ...accountData
    });
  }, []);

  // Common event tracking functions
  const trackCompanyConnect = useCallback(async (companyId, companyName = '') => {
    await logEvent('company_connect_attempted', {
      company_id: companyId,
      company_name: companyName,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackVideoUpload = useCallback(async (videoType = 'intro') => {
    await logEvent('video_uploaded', {
      video_type: videoType,
      upload_source: 'user_recording',
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackJobReaction = useCallback(async (jobId, reaction) => {
    await logEvent('job_reaction', {
      job_id: jobId,
      reaction: reaction, // 'like' or 'dislike'
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackProfileUpdate = useCallback(async (updateType) => {
    await logEvent('profile_updated', {
      update_type: updateType,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackNavigation = useCallback(async (destination, source = '') => {
    await logEvent('navigate', {
      destination,
      source,
      timestamp: new Date().toISOString()
    });
  }, []);

  return {
    // Core functions
    trackEvent,
    trackPage,
    setUser,
    logEvent: trackEvent, // Alias for direct event logging
    
    // Specific tracking functions
    trackQuizCompletion,
    trackAccountCreation,
    trackCompanyConnect,
    trackVideoUpload,
    trackJobReaction,
    trackProfileUpdate,
    trackNavigation
  };
}; 